import {
  NativeStackHeaderProps,
  Stack,
  useLocalSearchParams,
  useRouter,
} from 'expo-router';
import { useTabaPayDebitCardFundingForm } from 'features/moveMoney/screens/dcf/hooks/useTabaPayDebitCardFundingForm';
import {
  getExternalAddressText,
  getFlowNameForAnalytics,
  TabaPayEntryFlowType,
} from 'features/moveMoney/screens/dcf/tabapay/helpers/TabaPay.helpers';
import TabapayTransferScreenV2 from 'features/moveMoney/screens/dcf/tabapay/TabapayTransfer/TabapayTransferScreenV2';
import VerifyCardScreen from 'features/moveMoney/screens/dcf/tabapay/VerifyCardScreen';
import { formatExpDate } from 'features/v2a/receiver/helpers/V2A.helpers';
import React, { useRef, useState } from 'react';
import { Controller } from 'react-hook-form';
import { ScrollView, StyleSheet, TextInput, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import Button from 'shared/components/vxl/Button/Button';
import HairLineDivider from 'shared/components/vxl/divider/HairlineDivider/HairLineDivider';
import IconButton from 'shared/components/vxl/icon/IconButton/IconButton';
import { IllustrationKeyType } from 'shared/components/vxl/Illustration/Illustration';
import VXLDropdown, {
  VXL_DROPDOWN_MENU_RESERVED_SPACE,
} from 'shared/components/vxl/input/VXLDropdown/VXLDropdown';
import VXLTextInput from 'shared/components/vxl/input/VXLTextInput/VXLTextInput';
import FullScreenLoading from 'shared/components/vxl/loading/FullScreenLoading/FullScreenLoading';
import ExpoNavigationBar from 'shared/components/vxl/navigation/ExpoNavigationBar/ExpoNavigationBar';
import BodyBaseText from 'shared/components/vxl/typography/primary/body/BodyBaseText';
import BodyXSText from 'shared/components/vxl/typography/primary/body/BodyXSText';
import TitleXLText from 'shared/components/vxl/typography/primary/title/TitleXLText';
import { isWeb } from 'shared/constants/platform';
import { navigateBack } from 'shared/helpers/backNavigation.helpers';
import ErrorScreen, {
  ErrorScreenType,
} from 'shared/patterns/vxl/errors/ErrorScreen/ErrorScreen';
import ScreenTransitionOverlay from 'shared/patterns/vxl/ScreenTransitionOverlay/ScreenTransitionOverlay';
import theme from 'shared/styles/theme';
import { isValidCardNumber } from 'shared/utils/string/cardNumber';
import { filterDigits } from 'shared/utils/string/string.helpers';
import useVaroOnboardingFlow from 'shared/utils/useVaroOnboardingFlow/useVaroOnboardingFlow';

const TabaPayDebitCardFundingForm = () => {
  const router = useRouter();
  const { tabaPayEntryFlowType = TabaPayEntryFlowType.ExternalAccount } =
    useLocalSearchParams<{
      tabaPayEntryFlowType?: TabaPayEntryFlowType;
    }>();

  const { top, bottom } = useSafeAreaInsets();
  const scrollViewRef = useRef<ScrollView>(null);
  const [isCvvVisible, setCvvVisible] = useState(false);
  const cardNumberInputRef = useRef<TextInput | null>(null);
  const dateInputRef = useRef<TextInput | null>(null);
  const cvvInputRef = useRef<TextInput | null>(null);

  const {
    combinedErrorScreen,
    isVerifyCardScreenShown,
    fullName,
    title,
    description,
    disclaimer,
    addNewAddressOnPressEvent,
    control,
    cancelTransferLoading,
    verifyCardActionScreen,
    shouldShowLoader,
    setIsVerifyCardScreenShown,
    setShouldRefreshCards,
    runThreeDS,
    translatedText,
    handleErrorPrimaryButtonOnPress,
    handleErrorButtonPress,
    errors,
    selectedAddress,
    finalDropdownAddressList,
    setSelectedAddress,
    handleSubmitAction,
    isValid,
    handleCardNumberInputErrorTriggered,
    handleExpiryDateErrorTriggered,
    handleCvvInputErrorTriggered,
    trigger,
    clearAllErrors,
    handleCloseError,
  } = useTabaPayDebitCardFundingForm();

  const flowName = getFlowNameForAnalytics(tabaPayEntryFlowType);

  const analyticsCommonAttributes = {
    subcategory: flowName,
    flow_name: flowName,
  };

  const flow = useVaroOnboardingFlow();

  const shouldShowBackButton = !shouldShowLoader;
  const shouldShowHeader =
    shouldShowBackButton && !(isWeb && !!combinedErrorScreen);

  if (tabaPayEntryFlowType === TabaPayEntryFlowType.DCFFlow) {
    return <TabapayTransferScreenV2 />;
  } else if (isVerifyCardScreenShown && verifyCardActionScreen) {
    return (
      <VerifyCardScreen
        title={verifyCardActionScreen.title}
        contentText={verifyCardActionScreen.message}
        buttonLabel={verifyCardActionScreen.button.buttonText}
        onBackClick={() => {
          setIsVerifyCardScreenShown(false);
          setShouldRefreshCards(true);
          navigateBack();
        }}
        onContinue={runThreeDS}
        tabaPayEntryFlowType={tabaPayEntryFlowType}
        illustrationKey={verifyCardActionScreen.image as IllustrationKeyType}
      />
    );
  }

  return (
    <>
      <Stack.Screen
        options={{
          headerShown: shouldShowHeader,
          headerBackVisible: shouldShowHeader,
          header: (props: NativeStackHeaderProps) => (
            <ExpoNavigationBar {...props} primaryOnPress={navigateBack} />
          ),
        }}
      />
      <ScreenTransitionOverlay
        isLoading={shouldShowLoader}
        LoadingComponent={<FullScreenLoading isCoinLoader />}
        ErrorComponent={
          combinedErrorScreen ? (
            <View
              testID="dcf-form-error-container"
              style={isWeb ? styles.errorHeaderFixed : styles.errorHeader}
            >
              <View style={[styles.closeIconWrapper, { top: top }]}>
                <IconButton
                  iconKey="CLOSE"
                  iconSize={24}
                  iconOnPress={handleCloseError}
                  iconButtonAccessibilityLabel="close"
                />
              </View>
              <ErrorScreen
                screenType={ErrorScreenType.Error}
                messageHeadline={combinedErrorScreen.messageHeadline}
                messageBody={combinedErrorScreen.messageBody}
                primaryButton={{
                  title:
                    combinedErrorScreen.primaryButton?.buttonText ||
                    translatedText('closeButton'),
                  onPress: handleErrorPrimaryButtonOnPress,
                }}
                secondaryButton={
                  combinedErrorScreen.secondaryButton
                    ? {
                        title: combinedErrorScreen.secondaryButton.buttonText,
                        onPress: () => {
                          clearAllErrors();
                          handleErrorButtonPress(
                            combinedErrorScreen.secondaryButton,
                          );
                        },
                        isLoading: cancelTransferLoading,
                      }
                    : undefined
                }
              />
            </View>
          ) : null
        }
      />
      <View style={styles.container}>
        <ScrollView
          ref={scrollViewRef}
          keyboardShouldPersistTaps="handled"
          pointerEvents={shouldShowLoader ? 'none' : 'auto'}
          contentContainerStyle={styles.formContainer}
        >
          <TitleXLText numberOfLines={0}>{title}</TitleXLText>
          <BodyBaseText numberOfLines={0}>{description}</BodyBaseText>
          <VXLTextInput
            editable={false}
            labelText={translatedText('nameOnCard')}
            value={fullName}
            testID="text-input-nameOnCard"
          />
          <Controller
            control={control}
            name="cardNumber"
            render={({ field: { onChange, onBlur, value } }) => {
              const handleCardNumberChange = (text: string) => {
                const cleaned = text.replace(/[.-]/g, '');
                const digits = cleaned.replace(/\D/g, '');
                const formatted = digits.replace(/(.{4})/g, '$1 ').trim();
                onChange(formatted);
                void trigger('cardNumber');
                if (isValidCardNumber(digits)) {
                  dateInputRef.current?.focus?.();
                }
                onBlur();
              };

              return (
                <VXLTextInput
                  ref={cardNumberInputRef}
                  labelText={translatedText('cardNumber')}
                  onChangeText={handleCardNumberChange}
                  onBlur={handleCardNumberInputErrorTriggered}
                  placeholder={translatedText('cardNumber')}
                  keyboardType="number-pad"
                  returnKeyType="done"
                  value={value}
                  autoFocus
                  autoCapitalize="none"
                  maxLength={19}
                  errorText={errors.cardNumber?.message ?? ''}
                  onSubmitEditing={() => {
                    dateInputRef.current?.focus?.();
                  }}
                  analyticsId="cardNumberFieldClicked"
                  customAttributes={{
                    card_type: 'debit_card',
                    ...analyticsCommonAttributes,
                  }}
                  editable={!shouldShowLoader}
                  autoComplete="cc-number"
                  textContentType="creditCardNumber"
                  testID="text-input-cardNumber"
                />
              );
            }}
          />
          <View style={styles.formLine}>
            <Controller
              control={control}
              name="date"
              render={({ field: { onChange, onBlur, value } }) => {
                const handleChange = async (text: string) => {
                  onChange(text);
                  const isDateValid = await trigger('date');
                  if (isDateValid && text.length === 5) {
                    cvvInputRef.current?.focus?.();
                  }
                  onBlur();
                };
                return (
                  <View style={styles.input}>
                    <VXLTextInput
                      ref={dateInputRef}
                      labelText={translatedText('expDate')}
                      errorText={errors.date?.message ?? ''}
                      onChangeText={async (text) => {
                        await handleChange(
                          formatExpDate(text.replace(/\D/g, '')),
                        );
                      }}
                      onFocus={() => {
                        scrollViewRef.current?.scrollToEnd();
                      }}
                      onBlur={() => {
                        handleExpiryDateErrorTriggered();
                      }}
                      value={value}
                      returnKeyType="done"
                      placeholder={translatedText('dateHint')}
                      keyboardType="number-pad"
                      maxLength={5}
                      autoComplete="cc-exp"
                      textContentType="creditCardExpiration"
                      onSubmitEditing={() => {
                        cvvInputRef.current?.focus?.();
                      }}
                      analyticsId="expDateFieldClicked"
                      customAttributes={{
                        card_type: 'debit_card',
                        ...analyticsCommonAttributes,
                      }}
                      editable={!shouldShowLoader}
                      testID="text-input-expDate"
                    />
                  </View>
                );
              }}
            />
            <Controller
              control={control}
              name="cvv"
              render={({ field: { onChange, onBlur, value } }) => (
                <View style={styles.input}>
                  <VXLTextInput
                    ref={cvvInputRef}
                    labelText={translatedText('tabaPayValidation.securityCode')}
                    onChangeText={(text) => {
                      onChange(filterDigits(text));
                      onBlur();
                    }}
                    inputMode="numeric"
                    maxLength={3}
                    returnKeyType="done"
                    autoComplete="cc-csc"
                    textContentType="creditCardSecurityCode"
                    value={value}
                    placeholder={translatedText(
                      'tabaPayValidation.securityCode',
                    )}
                    autoCapitalize="none"
                    errorText={errors.cvv?.message ?? ''}
                    onBlur={() => {
                      handleCvvInputErrorTriggered();
                    }}
                    secureTextEntry={!isCvvVisible}
                    rightIcon={{
                      iconKey: !isCvvVisible ? 'VIEW' : 'HIDE',
                      iconAccessibilityLabel: isCvvVisible
                        ? 'eye-close'
                        : 'eye-open',
                      onPress: () => {
                        setCvvVisible(!isCvvVisible);
                      },
                    }}
                    analyticsId="cvvFieldClicked"
                    customAttributes={{
                      card_type: 'debit_card',
                      ...analyticsCommonAttributes,
                    }}
                    onFocus={() => {
                      scrollViewRef.current?.scrollToEnd();
                    }}
                    editable={!shouldShowLoader}
                    testID="text-input-tabaPayValidation.securityCode"
                  />
                </View>
              )}
            />
          </View>
          <Controller
            control={control}
            name="address"
            render={({ field: { onChange, onBlur, value } }) => (
              <VXLDropdown
                labelText={translatedText('address')}
                placeholder={translatedText('address')}
                value={value || getExternalAddressText(selectedAddress)}
                options={[
                  ...finalDropdownAddressList.map(getExternalAddressText),
                  translatedText('newAddress'),
                ]}
                onChangeText={(text) => {
                  if (text === translatedText('newAddress')) {
                    addNewAddressOnPressEvent();
                    router.push(
                      `/${flow}/tabapay/address-screen?tabaPayEntryFlowType=${tabaPayEntryFlowType}`,
                    );
                    return;
                  }
                  const selectedIndex = finalDropdownAddressList.findIndex(
                    (address) => getExternalAddressText(address) === text,
                  );
                  if (selectedIndex >= 0) {
                    setSelectedAddress(finalDropdownAddressList[selectedIndex]);
                  }
                  onChange(text);
                  onBlur();
                }}
                analyticsId="addressFieldClicked"
                customAttributes={{
                  card_type: 'debit_card',
                  ...analyticsCommonAttributes,
                }}
                testID="address-select"
              />
            )}
          />
          {/* Give dropdown enough space at the bottom to avoid being cut off */}
          <View style={styles.bottomScrollPadding}></View>
        </ScrollView>
      </View>
      <View style={styles.dividerWrapper}>
        <HairLineDivider />
      </View>
      <View
        style={[
          styles.buttonContainer,
          {
            paddingBottom: bottom,
          },
        ]}
      >
        <BodyXSText style={{ color: theme.color.text.secondary }}>
          {disclaimer}
        </BodyXSText>
        <View style={styles.buttonStyle}>
          <Button
            label={translatedText('next')}
            isDisabled={!isValid}
            onPress={handleSubmitAction}
            isLoading={shouldShowLoader}
            size="LARGE"
            type="PRIMARY"
            width="FULL"
          />
        </View>
      </View>
    </>
  );
};

const styles = StyleSheet.create({
  bottomScrollPadding: {
    height: VXL_DROPDOWN_MENU_RESERVED_SPACE,
  },
  buttonContainer: {
    backgroundColor: theme.color.background.primary,
    paddingHorizontal: theme.spacing.m,
  },
  buttonStyle: {
    paddingVertical: theme.spacing.m,
  },
  closeIconWrapper: {
    left: theme.spacing.xs,
    position: 'absolute',
    zIndex: 10,
  },
  container: {
    backgroundColor: theme.color.background.primary,
    flex: 1,
  },
  dividerWrapper: {
    backgroundColor: theme.color.background.primary,
    paddingBottom: theme.spacing.m,
  },
  errorHeader: {
    flex: 1,
    height: '100%',
    width: '100%',
  },
  errorHeaderFixed: {
    backgroundColor: theme.color.background.primary,
    bottom: 0,
    left: 0,
    position: 'absolute',
    right: 0,
    top: 0,
    zIndex: 9999,
  },
  formContainer: {
    gap: theme.spacing.m,
    padding: theme.spacing.m,
  },
  formLine: {
    flexDirection: 'row',
    gap: theme.spacing.m,
    justifyContent: 'space-between',
  },
  input: {
    flex: 1,
  },
});

export default TabaPayDebitCardFundingForm;
